import type { AllowedButtons, DriveStep } from 'driver.js';
import type { createGates } from './tourGates';

const noNext: { showButtons: AllowedButtons[] } = { showButtons: ['close'] };

// Gate keys for this shared block use a distinct numeric range (101+) so
// they can never collide with whatever keys the caller uses for its own
// steps (e.g. End Session, Tutorial button) within the same createGates()
// call — keys only need to be unique per call, not match real array
// position (see createGates in tourGates.ts).
const KEY_PICK_SCENARIO = 101;
const KEY_CLICK_NEXT = 102;
const KEY_CHOOSE_MODE = 103;
const KEY_PERSONALIZE_START = 104;
const KEY_PICK_STUDENT = 105;
const KEY_SESSION_STARTING = 106;
const KEY_MIC = 107;
const KEY_STEP_GUIDE = 108;
const KEY_TOOLBAR_TOGGLE = 109;
const KEY_TRANSCRIPT_TOGGLE = 110;
const KEY_MIC_WAIT = 111;
const KEY_INTRO_AUDIO = 112;
const KEY_BRIEFING = 113;
const KEY_PICK_SCENARIO_DETAIL = 114;

/**
 * The scenario-picker wizard: pick a scenario through starting the session.
 * Only meaningful when no session is live yet — the Page Tutorial skips this
 * block entirely (via buildLiveSessionSteps) when opened mid-session (see
 * pageSteps.ts's buildRolePlayPageSteps).
 *
 * gates must come from a single `createGates(driverRef)` call shared with
 * whatever other gated steps the caller appends after this block (e.g. End
 * Session), since gate keys index into one common cleanupByStep map.
 */
export function buildScenarioWizardSteps(gates: ReturnType<typeof createGates>): DriveStep[] {
  const { gateOnDom, gateOnDomUnlessFlag } = gates;

  return [
    {
      // The highlighted element also defines the cutout in driver.js's dark
      // overlay (the only area still clickable) — it must stay the full
      // overlay for every wizard sub-step below, or the scenario cards /
      // difficulty tiles / Next button fall outside the cutout and stop
      // receiving clicks entirely. The overlay spans the whole viewport
      // though, so driver.js has no room to place the popover beside it and
      // falls back to pinning it at the bottom of the screen — on mobile
      // that lands right on top of the selector's own Next/Start Role Play
      // button. popoverClass + the matching CSS rule in tutorial.scss forces
      // the popover panel itself (not the stage/cutout) to dock near the top
      // on narrow screens instead, clear of both the card list and the
      // footer button.
      element: '#tour-scenario-selector',
      popover: {
        title: 'Pick a Scenario',
        description: 'Choose a Role-Play Scenario to practice.',
        popoverClass: 'tour-scenario-popover',
        ...noNext,
      },
      // Fires once a card is actually selected — distinct from the "Click
      // Next" step below, which covers the separate act of advancing past
      // this stage (the Next button can stay disabled a moment after
      // picking a card while details load in the background).
      ...gateOnDom(KEY_PICK_SCENARIO, '.option-card.selected', 'appear'),
    },
    {
      element: '#tour-scenario-selector',
      popover: {
        title: 'Click Next',
        description: 'Wait for the Next button to become enabled ✅ (details may still be loading), then click it to continue.',
        popoverClass: 'tour-scenario-popover',
        ...noNext,
      },
      // #tour-scenario-cards only exists in the DOM during the card-picking
      // stage — this fires once the user moves past it (to the difficulty
      // stage, personalize stage, or straight to closing the wizard).
      ...gateOnDom(KEY_CLICK_NEXT, '#tour-scenario-cards', 'disappear'),
    },
    {
      element: '#tour-scenario-selector',
      popover: {
        title: 'Choose a Scenario',
        description:
          'Some practices let you choose the situation to play out. Pick one from the list, or choose "Other" to describe your own. Not applicable here? This step just skips ahead.',
        popoverClass: 'tour-scenario-popover',
        ...noNext,
      },
      // Same auto-skip shape as the difficulty step below: #tour-scenario-picker never
      // mounts for configs that carry their own scenario, so the "disappear" check is
      // already true and this advances immediately.
      ...gateOnDom(KEY_PICK_SCENARIO_DETAIL, '#tour-scenario-picker', 'disappear'),
    },
    {
      element: '#tour-scenario-selector',
      popover: {
        title: 'Choose a Mode',
        description:
          'Some scenarios let you pick a difficulty before you start. If so, wait for Next to enable, then continue. Not applicable here? This step just skips ahead.',
        popoverClass: 'tour-scenario-popover',
        ...noNext,
      },
      // #tour-difficulty-selector never mounts for scenarios that don't
      // support difficulty, so waitForDom's "disappear" check is already
      // true and this advances almost immediately — a clean auto-skip with
      // no extra branching needed.
      ...gateOnDom(KEY_CHOOSE_MODE, '#tour-difficulty-selector', 'disappear'),
    },
    {
      element: '#tour-scenario-selector',
      popover: {
        title: 'Personalize & Start',
        description: 'Optionally set a preferred name and session notes, then click "Start Role Play" to begin.',
        popoverClass: 'tour-scenario-popover',
        ...noNext,
      },
      ...gateOnDom(KEY_PERSONALIZE_START, '#tour-scenario-selector', 'disappear'),
    },
    {
      // The briefing (SessionBriefing.tsx) mounts in the same render that closes the
      // wizard, so the previous step's "#tour-scenario-selector disappeared" gate and
      // this step's target hand over with no gap for the tour to fall through.
      element: '#tour-session-briefing',
      popover: {
        title: 'Read your briefing',
        description:
          "Here's what you picked for this session: the situation, how long you have, and how to begin. Click \"I'm ready to start\" when you've read it.",
        // Same reason as the wizard steps above: the highlighted element is a
        // full-viewport overlay, so driver.js has nowhere to put the popover beside
        // it and pins it to the bottom — right on the briefing's own start button on
        // a narrow screen. This docks it at the top instead.
        popoverClass: 'tour-scenario-popover',
        ...noNext,
      },
      // 'disappear' is already true when no briefing is up, so this auto-skips rather
      // than hanging — the same self-skipping shape as the difficulty stage above.
      ...gateOnDom(KEY_BRIEFING, '#tour-session-briefing', 'disappear'),
    },
    {
      element: '#tour-classroom-scene',
      popover: {
        title: 'Intro clip',
        description:
          "Some scenarios play a short intro clip from the student before you begin. If one starts, listen for it to finish. Not applicable here? This step just skips ahead.",
        ...noNext,
      },
      // Whether an intro clip plays at all depends on the scenario
      // (selections.intro_prompt in handleSelectionComplete,
      // GeminiDirectStreamPage.tsx) and isn't knowable when this step array
      // is built, well before the user has even picked a scenario — so this
      // has to be a runtime check, same as KEY_CHOOSE_MODE's auto-skip above.
      // data-tour-phase on #tour-classroom-scene is 'pre-connect' for
      // exactly as long as the clip is playing/loading (regardless of
      // auto-start vs manual-start). If it's already something else when
      // this step is reached, no clip is playing (either there is none, or
      // it already finished) and 'disappear' is immediately true, so this
      // auto-skips with no extra branching — mirroring #tour-difficulty-selector
      // above. If it is 'pre-connect', this waits for the clip to finish.
      ...gateOnDom(KEY_INTRO_AUDIO, '#tour-classroom-scene[data-tour-phase="pre-connect"]', 'disappear'),
    },
    {
      element: '#tour-classroom-scene',
      popover: {
        title: 'Pick a student',
        description: "If the session hasn't already started, click on a student avatar to start your roleplay session.",
        ...noNext,
      },
      // Auto-start scenarios never need a click at all (see
      // handleSelectionComplete, GeminiDirectStreamPage.tsx) — data-tour-auto-start
      // reflects that synchronously (set in the same render that closes the
      // selector, so there's no race), letting this skip straight ahead
      // instead of telling the user to click an avatar that will never need
      // clicking. For manual-start scenarios, gating on data-tour-phase simply
      // leaving 'idle' doesn't work: the previous step's intro-clip wait can
      // leave the phase back at 'idle' with no click having happened yet, so
      // "not idle" can't tell a real click apart from a clip merely having
      // played. Gating on 'connecting' / 'greeting' / 'live' instead only
      // fires once the session is actually starting, i.e. once a real click
      // happened.
      ...gateOnDomUnlessFlag(
        KEY_PICK_STUDENT,
        '#tour-classroom-scene',
        'data-tour-auto-start',
        '#tour-classroom-scene[data-tour-phase="connecting"], #tour-classroom-scene[data-tour-phase="greeting"], #tour-classroom-scene[data-tour-phase="live"]'
      ),
    },
    {
      element: '#tour-classroom-scene',
      popover: {
        title: 'Starting your session',
        description: 'Your roleplay session is starting. Please wait a moment.',
        ...noNext,
      },
      ...gateOnDom(KEY_SESSION_STARTING, '#tour-mic-button', 'appear'),
    },
  ];
}

/**
 * The live-session controls: mic, step guide, toolbar, and transcript — shown
 * once a session is connected. Shared by the FTUE interactive tour
 * (interactiveSteps.ts, reached via the scenario wizard above) and the Role
 * Play "Page Tutorial" (pageSteps.ts), which also jumps straight here,
 * skipping the wizard, when opened while a session is already in progress.
 *
 * gates must come from a single `createGates(driverRef)` call shared with
 * whatever other gated steps the caller appends after this block (e.g. End
 * Session), since gate keys index into one common cleanupByStep map.
 *
 * @param waitForMic - Pass `true` only when this block is entered directly,
 * without the scenario wizard immediately before it (pageSteps.ts's
 * sessionInProgress/isTransitioning jump). The wizard's own "Starting your
 * session" step (above) already gates on #tour-mic-button appearing before
 * handing off here, so the FTUE tour and the wizard-then-live Page Tutorial
 * path (buildRolePlaySharedSteps) must leave this `false` (the default) —
 * otherwise, since steps are all built once up front, before a session is
 * even connected, this would always find #tour-mic-button missing and
 * duplicate that same wait step immediately after the wizard's own.
 */
export function buildLiveSessionSteps(gates: ReturnType<typeof createGates>, waitForMic = false): DriveStep[] {
  const { gateOnClick, gateOnClickOrSkip, gateOnDom } = gates;

  // The Page Tutorial can jump straight into this block mid-session (see
  // buildRolePlayPageSteps's sessionInProgress check, which also counts the
  // few-second 'greeting' window as "in progress" so the tour doesn't wrongly
  // show the full scenario wizard). #tour-mic-button doesn't mount until the
  // greeting audio finishes, so if it's opened in that window, wait for it
  // explicitly with its own step — mirroring "Starting your session" above —
  // instead of falling through to gateOnClickOrSkip's "genuinely absent, skip
  // ahead" fallback below. Unlike the Step Guide step (optional per
  // scenario), the mic button always eventually mounts once connected; it's
  // never actually absent, only briefly delayed.
  const micWaitStep: DriveStep[] =
    waitForMic && !document.querySelector('#tour-mic-button')
      ? [
          {
            element: '#tour-classroom-scene',
            popover: {
              title: 'Starting your session',
              description: 'Your roleplay session is starting. Please wait a moment.',
              ...noNext,
            },
            ...gateOnDom(KEY_MIC_WAIT, '#tour-mic-button', 'appear'),
          },
        ]
      : [];

  return [
    ...micWaitStep,
    {
      element: '#tour-mic-button',
      popover: {
        title: 'Your microphone',
        description: 'Click here to mute or unmute your mic during the session.',
        ...noNext,
      },
      ...gateOnClickOrSkip(KEY_MIC, '#tour-mic-button'),
    },
    {
      element: '#tour-network-quality',
      popover: {
        title: 'Connection status',
        description: "Shows how your connection is doing during the call: Good, Medium, or Bad. If it turns Medium or Bad, that's your network, not the app.",
        showButtons: ['next', 'close'],
      },
      // No gate needed — the badge is always present once connected, nothing
      // to click or wait for (unlike the mic button or step guide above,
      // which can be genuinely absent depending on scenario/state).
    },
    {
      element: '#tour-step-guide',
      popover: {
        title: 'Step Guide',
        description:
          "Track your progress through this scenario's teaching steps. Click Steps to open the guide. No steps for this scenario? This continues automatically.",
        ...noNext,
      },
      // #tour-step-guide is absent for scenarios with no configured teaching
      // steps (or in HARD difficulty) — gateOnClickOrSkip auto-advances past
      // this step instead of waiting forever for a click that can't happen.
      ...gateOnClickOrSkip(KEY_STEP_GUIDE, '#tour-step-guide'),
    },
    {
      element: '.toolbar-toggle',
      popover: {
        title: 'More options',
        description: 'Click here to see more options.',
        ...noNext,
      },
      // OrSkip, not a plain click gate: on a compact stage CollapsibleToolbar is
      // rendered alwaysExpanded and never draws this chevron — there is nothing to
      // collapse, every button is already on screen. A gateOnClick there would park
      // a first-time mobile user on "Click here to see more options" with nothing to
      // click and no Next button (noNext), leaving closing the tour as the only exit.
      // Skipping lands on the transcript step, which on a compact stage is already
      // visible — which is the point the toggle step exists to reach on desktop.
      ...gateOnClickOrSkip(KEY_TOOLBAR_TOGGLE, '.toolbar-toggle'),
    },
    {
      element: '#tour-transcript-toggle',
      popover: {
        title: 'Live Transcript',
        description: 'Click here to open a live transcript of your conversation.',
        ...noNext,
      },
      ...gateOnClick(KEY_TRANSCRIPT_TOGGLE, '#tour-transcript-toggle'),
    },
    {
      element: '.tour-transcript-panel',
      popover: {
        title: 'Transcription',
        description: "Here's your live transcript, updating as you talk.",
        showButtons: ['next', 'close'],
      },
    },
  ];
}

/**
 * The full 13-step "live session" walkthrough shared by both the FTUE
 * interactive tour (interactiveSteps.ts) and the Role Play "Page Tutorial"
 * (pageSteps.ts) — scenario wizard through to the live transcript. Both tours
 * prepend a single non-gated welcome step (and, for the Page Tutorial, an
 * optional lead-in step) before this block and append their own (different)
 * ending. The Page Tutorial calls buildLiveSessionSteps directly (skipping
 * the wizard half) instead of this combined export when a session is already
 * in progress — see pageSteps.ts's buildRolePlayPageSteps.
 */
export function buildRolePlaySharedSteps(gates: ReturnType<typeof createGates>): DriveStep[] {
  return [...buildScenarioWizardSteps(gates), ...buildLiveSessionSteps(gates)];
}
